import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Typography,
    TextField,
    Button,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    IconButton
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import { makeStyles } from '@mui/styles';
import { api } from 'common';
import moment from 'moment/min/moment-with-locales';
import AlertDialog from '../components/AlertDialog';
import CircularLoading from '../components/CircularLoading';

const useStyles = makeStyles(() => ({
    container: {
        backgroundColor: 'var(--bg-main)',
        minHeight: '100vh',
        padding: '24px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start'
    },
    card: {
        borderRadius: '20px',
        backgroundColor: 'var(--bg-card)',
        border: 'var(--card-border)',
        boxShadow: 'var(--shadow)',
        width: '100%',
        maxWidth: '900px',
        padding: '30px',
        marginTop: '20px'
    },
    textField: {
        marginBottom: '15px',
        "& label.Mui-focused": {
            color: '#E9AE31',
        },
        "& .MuiOutlinedInput-root": {
            "&.Mui-focused fieldset": {
                borderColor: '#E9AE31',
            },
            "& input": {
                color: 'var(--text-main)'
            }
        },
        "& .MuiInputLabel-root": {
            color: 'var(--text-secondary)'
        }
    },
    summaryCard: {
        marginTop: '10px',
        marginBottom: '10px',
        padding: '16px 20px',
        borderRadius: '14px',
        border: '1px solid rgba(233, 174, 49, 0.35)',
        backgroundColor: 'rgba(233, 174, 49, 0.06)',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px'
    },
    row: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: 'rgba(233, 174, 49, 0.06)'
        }
    }
}));

// Admin tool: lands directly on every batch ever generated (paginated), instead of requiring the
// batch number to be typed/known up front. Clicking a batch drills into that batch's full serial
// list, in print order - for auditing/backtracking a whole run, not just one scanned sticker.
export default function BatchDetails() {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const classes = useStyles();
    const dispatch = useDispatch();

    const { getAllBatches, getSerialsByBatch } = api;

    const [loading, setLoading] = useState(true);
    const [batches, setBatches] = useState([]);
    const [filter, setFilter] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [detailLoading, setDetailLoading] = useState(false);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [alertState, setAlertState] = useState({ open: false, msg: '' });

    useEffect(() => {
        (async () => {
            setLoading(true);
            const res = await dispatch(getAllBatches());
            setLoading(false);
            if (res && res.success) {
                setBatches(res.batches || []);
            } else {
                setAlertState({ open: true, msg: (res && res.error) || t('batch_details_not_found') });
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAlertClose = (e) => {
        e.preventDefault();
        setAlertState({ open: false, msg: '' });
    };

    const filteredBatches = batches.filter(b =>
        b.batchNumber.toLowerCase().includes(filter.trim().toLowerCase())
    );
    const pagedBatches = filteredBatches.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const handleChangePage = (e, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const handleViewBatch = async (batchNumber) => {
        setDetailLoading(true);
        const res = await dispatch(getSerialsByBatch(batchNumber));
        setDetailLoading(false);
        if (res && res.success && res.serials && res.serials.length > 0) {
            setSelectedBatch(res);
        } else {
            setAlertState({ open: true, msg: (res && res.error) || t('batch_details_not_found') });
        }
    };

    // Reconstructs the exact "<batchNumber>-<seq>" label printed on that unit's sticker
    // pair/packaging box (see unitLabel in GenerateQr.js) from the stored batchSequence -
    // padding width is based on the highest batchSequence actually in this batch, not just
    // its count, since batchSequence can start above 1 (the "Starting Batch Sequence #"
    // field) and so can run past what the raw count alone would suggest.
    const unitLabelDigits = selectedBatch
        ? Math.max(3, String(Math.max(0, ...selectedBatch.serials.map(s => s.batchSequence || 0))).length)
        : 3;
    const unitLabelFor = (s) => (s.batchSequence
        ? `${selectedBatch.batchNumber}-${String(s.batchSequence).padStart(unitLabelDigits, '0')}`
        : '—');

    const handleExportCsv = () => {
        if (!selectedBatch || !selectedBatch.serials || selectedBatch.serials.length === 0) return;
        const rows = [
            ['Batch No.', 'Serial Number', 'Created At'],
            ...selectedBatch.serials.map(s => [
                unitLabelFor(s),
                s.serialNumber,
                s.createdAt ? moment(s.createdAt).format('YYYY-MM-DD HH:mm:ss') : ''
            ])
        ];
        const csv = rows.map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${selectedBatch.batchNumber}_serials.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    if (loading) return <CircularLoading />;

    return (
        <div className={classes.container}>
            <Card className={classes.card}>
                <CardContent style={{ padding: '10px' }}>
                    <Typography
                        variant="h5"
                        style={{
                            marginBottom: '20px',
                            textAlign: isRTL === 'rtl' ? 'right' : 'left',
                            fontWeight: 'bold',
                            fontFamily: 'Inter, sans-serif',
                            color: 'var(--text-main)'
                        }}
                    >
                        {t('batch_details')}
                    </Typography>

                    {!selectedBatch ? (
                        <>
                            <TextField
                                value={filter}
                                onChange={(e) => { setFilter(e.target.value); setPage(0); }}
                                fullWidth
                                className={classes.textField}
                                label={t('filter_batches')}
                                placeholder="e.g. BATCH001"
                            />

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ color: 'var(--text-secondary)' }}>{t('batch_number')}</TableCell>
                                            <TableCell style={{ color: 'var(--text-secondary)' }}>{t('serial_series')}</TableCell>
                                            <TableCell style={{ color: 'var(--text-secondary)' }}>{t('serial_count')}</TableCell>
                                            <TableCell style={{ color: 'var(--text-secondary)' }}>Generated</TableCell>
                                            <TableCell style={{ color: 'var(--text-secondary)' }} />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pagedBatches.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={5} style={{ color: 'var(--text-secondary)', textAlign: 'center', padding: '30px' }}>
                                                    {t('batch_details_not_found')}
                                                </TableCell>
                                            </TableRow>
                                        ) : pagedBatches.map((b) => (
                                            <TableRow key={b.batchNumber} className={classes.row} onClick={() => handleViewBatch(b.batchNumber)}>
                                                <TableCell style={{ color: 'var(--text-main)', fontWeight: 'bold' }}>{b.batchNumber}</TableCell>
                                                <TableCell>
                                                    <Chip size="small" label={b.series} style={{ backgroundColor: 'rgba(233, 174, 49, 0.15)', color: '#E9AE31' }} />
                                                </TableCell>
                                                <TableCell style={{ color: 'var(--text-main)' }}>{b.count}</TableCell>
                                                <TableCell style={{ color: 'var(--text-secondary)' }}>{b.createdAt ? moment(b.createdAt).format('lll') : '—'}</TableCell>
                                                <TableCell align="right">
                                                    <IconButton size="small" style={{ color: '#E9AE31' }} onClick={(e) => { e.stopPropagation(); handleViewBatch(b.batchNumber); }}>
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TablePagination
                                component="div"
                                count={filteredBatches.length}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[10, 25, 50]}
                                style={{ color: 'var(--text-secondary)' }}
                            />
                        </>
                    ) : detailLoading ? (
                        <CircularLoading />
                    ) : (
                        <>
                            <div className={classes.summaryCard}>
                                <div>
                                    <Typography style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{t('batch_number')}</Typography>
                                    <Typography style={{ color: '#E9AE31', fontWeight: 'bold', fontSize: '20px' }}>{selectedBatch.batchNumber}</Typography>
                                </div>
                                <Chip
                                    label={`${selectedBatch.serials.length} ${t('serial_count')}`}
                                    style={{ backgroundColor: 'rgba(233, 174, 49, 0.15)', color: '#E9AE31', fontWeight: 'bold' }}
                                />
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<DownloadIcon />}
                                        onClick={handleExportCsv}
                                        style={{ borderColor: '#E9AE31', color: '#E9AE31', borderRadius: '19px' }}
                                    >
                                        {t('export_csv')}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<CloseIcon />}
                                        onClick={() => setSelectedBatch(null)}
                                        style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)', borderRadius: '19px' }}
                                    >
                                        {t('back')}
                                    </Button>
                                </div>
                            </div>

                            <TableContainer style={{ marginTop: '20px', maxHeight: '520px', overflowY: 'auto' }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ color: 'var(--text-secondary)', backgroundColor: 'var(--bg-card)' }}>Batch No.</TableCell>
                                            <TableCell style={{ color: 'var(--text-secondary)', backgroundColor: 'var(--bg-card)' }}>Serial Number</TableCell>
                                            <TableCell style={{ color: 'var(--text-secondary)', backgroundColor: 'var(--bg-card)' }}>Generated</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {selectedBatch.serials.map((s) => (
                                            <TableRow key={s.serialNumber}>
                                                <TableCell style={{ color: '#E9AE31', fontWeight: 'bold' }}>
                                                    {unitLabelFor(s)}
                                                </TableCell>
                                                <TableCell style={{ color: 'var(--text-main)' }}>{s.serialNumber}</TableCell>
                                                <TableCell style={{ color: 'var(--text-secondary)' }}>{s.createdAt ? moment(s.createdAt).format('lll') : '—'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </CardContent>
            </Card>

            <AlertDialog open={alertState.open} onClose={handleAlertClose}>
                {alertState.msg}
            </AlertDialog>
        </div>
    );
}
